import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useSaveBulkSuites } from 'src/pages/Property/CustomHooks/UseBulkSaveSuite/useSaveBulkSuite';

interface PropertyRequestDataContextType {
  saveBulkSuitesResponse: any[] | null;
  suitesError: string | null;
  isLoading: boolean;
  isSuccess: boolean;
}

interface PropertyRequestDataActionsContextType {
  fetchSaveBulkSuitesResponse: (
    suitesArray: any,
    propertyId: any,
    userID: any
  ) => Promise<boolean>;
  clearResponse: () => void;
}

const PropertyRequestDataContext = createContext<
  PropertyRequestDataContextType | undefined
>(undefined);
const PropertyRequestDataActionsContext = createContext<
  PropertyRequestDataActionsContextType | undefined
>(undefined);

interface PropertyRequestDataProviderProps {
  children: ReactNode;
}

export const PropertyRequestDataProvider: React.FC<
  PropertyRequestDataProviderProps
> = ({ children }) => {
  const bulkSaveSuitesActions = useSaveBulkSuites();

  const dataValue = useMemo(
    () => ({
      saveBulkSuitesResponse: bulkSaveSuitesActions.saveBulkSuitesResponse,
      suitesError: bulkSaveSuitesActions.suitesError,
      isLoading: bulkSaveSuitesActions.isLoading,
      isSuccess: bulkSaveSuitesActions.isSuccess,
    }),
    [
      bulkSaveSuitesActions.saveBulkSuitesResponse,
      bulkSaveSuitesActions.suitesError,
      bulkSaveSuitesActions.isLoading,
      bulkSaveSuitesActions.isSuccess,
    ]
  );

  const actionsValue = useMemo(
    () => ({
      fetchSaveBulkSuitesResponse:
        bulkSaveSuitesActions.fetchSaveBulkSuitesResponse,
      clearResponse: bulkSaveSuitesActions.clearResponse,
    }),
    [
      bulkSaveSuitesActions.fetchSaveBulkSuitesResponse,
      bulkSaveSuitesActions.clearResponse,
    ]
  );

  return (
    <PropertyRequestDataContext.Provider value={dataValue}>
      <PropertyRequestDataActionsContext.Provider value={actionsValue}>
        {children}
      </PropertyRequestDataActionsContext.Provider>
    </PropertyRequestDataContext.Provider>
  );
};

export const usePropertyRequestDataFromContext =
  (): PropertyRequestDataContextType => {
    const context = useContext(PropertyRequestDataContext);

    if (context === undefined) {
      throw new Error(
        'usePropertyRequestDataFromContext must be used within a PropertyRequestDataProvider'
      );
    }

    return context;
  };

export const usePropertyRequestDataActionsFromContext =
  (): PropertyRequestDataActionsContextType => {
    const context = useContext(PropertyRequestDataActionsContext);

    if (context === undefined) {
      throw new Error(
        'usePropertyRequestDataActionsFromContext must be used within a PropertyRequestDataProvider'
      );
    }

    return context;
  };
